import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import { Play, Square, Clock, Activity, Monitor, Globe } from 'lucide-react';
import ConfirmModal from '../components/ConfirmModal';
import useMarkAsReadOnMount from '../hooks/useMarkAsReadOnMount';
import './RemoteAttendance.css';

const RemoteAttendance = () => {
  useMarkAsReadOnMount(['attendance']);

  const [isActive, setIsActive] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [duration, setDuration] = useState('00:00:00');
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState({});
  const [message, setMessage] = useState({ type: '', text: '' });
  const durationTimerRef = useRef(null);

  const clearTimers = () => {
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
  };

  const calculateActivityStats = useCallback(() => {
    const end = Date.now();
    const start = sessionStartTime ? new Date(sessionStartTime).getTime() : end;
    const total = Math.max(0, end - start);
    return {
      sessionDuration: total,
      totalEvents: 0,
      totalActiveTime: total,
      totalInactiveTime: 0,
      activityPercentage: '100',
      startTime: new Date(start).toISOString(),
      endTime: new Date(end).toISOString(),
    };
  }, [sessionStartTime]);

  // The only way a session ends: the user stops it
  const endSession = useCallback(async () => {
    setStopping(true);
    try {
      const endTime = new Date();
      const sessionDuration = sessionStartTime ? endTime - new Date(sessionStartTime) : 0;
      const activityStats = calculateActivityStats();

      const response = await api.post('/attendance/remote/clock-out', {
        sessionId,
        clockOut: endTime.toTimeString().slice(0, 8),
        sessionDuration,
        activityStats,
      });

      if (response.success) {
        clearTimers();
        setIsActive(false);
        setSessionId(null);
        setSessionStartTime(null);
        setDuration('00:00:00');
        localStorage.removeItem('remoteAttendanceSession');
        setMessage({ type: 'success', text: 'Remote work session stopped and completed.' });
      }
    } catch (error) {
      console.error('Error ending session:', error);
      setMessage({ type: 'error', text: 'Error ending session: ' + (error.message || 'Unknown error') });
    } finally {
      setStopping(false);
      setShowStopConfirm(false);
    }
  }, [calculateActivityStats, sessionId, sessionStartTime]);

  // Load session from server (authoritative) and sync localStorage
  useEffect(() => {
    const bootstrapSession = async () => {
      try {
        const statusResponse = await api.get('/attendance/remote/status');
        if (statusResponse?.success && statusResponse?.status?.hasRecord && statusResponse?.status?.clockedIn && !statusResponse?.status?.clockedOut) {
          const sid = statusResponse.status.sessionId;
          const today = new Date().toISOString().slice(0, 10);
          const startTime = new Date(`${today} ${statusResponse.status.clockedIn}`);
          setSessionId(sid);
          setSessionStartTime(startTime);
          setIsActive(true);
          localStorage.setItem('remoteAttendanceSession', JSON.stringify({
            sessionId: sid,
            startTime: startTime.toISOString(),
            date: today,
            isActive: true,
          }));
          setMessage({ type: 'info', text: 'Resumed existing remote session.' });
        } else {
          // Server says no active session — clear any stale localStorage
          localStorage.removeItem('remoteAttendanceSession');
          setIsActive(false);
          setSessionId(null);
          setSessionStartTime(null);
        }
      } catch (error) {
        // If server check fails, fall back to localStorage for same-day session only
        const storedSession = localStorage.getItem('remoteAttendanceSession');
        if (storedSession) {
          try {
            const sessionData = JSON.parse(storedSession);
            const today = new Date().toISOString().slice(0, 10);
            if (sessionData.date === today && sessionData.isActive) {
              setSessionId(sessionData.sessionId);
              setSessionStartTime(new Date(sessionData.startTime));
              setIsActive(true);
            } else {
              localStorage.removeItem('remoteAttendanceSession');
            }
          } catch (e) {
            localStorage.removeItem('remoteAttendanceSession');
          }
        }
      }

      // Set device info
      setDeviceInfo({
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        screenSize: `${window.screen.width}x${window.screen.height}`
      });
    };

    bootstrapSession();
  }, []);

  // Update duration display
  useEffect(() => {
    clearInterval(durationTimerRef.current);
    if (isActive && sessionStartTime) {
      // Derived from the start time on every tick, so it stays right after the tab was in the background
      const tick = () => {
        const diff = Math.max(0, new Date() - sessionStartTime);
        const hours = Math.floor(diff / 3600000);
        const minutes = Math.floor((diff % 3600000) / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        setDuration(`${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
      };
      tick();
      durationTimerRef.current = setInterval(tick, 1000);
    }
    return () => clearInterval(durationTimerRef.current);
  }, [isActive, sessionStartTime]);

  const generateSessionId = () => {
    return 'remote_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  };

  const startSession = async () => {
    try {
      const newSessionId = generateSessionId();
      const startTime = new Date();
      
      const response = await api.post('/attendance/remote/clock-in', {
        sessionId: newSessionId,
        clockIn: startTime.toTimeString().slice(0, 8),
        date: startTime.toISOString().slice(0, 10),
        location: 'remote',
        deviceInfo: deviceInfo,
        browserInfo: { userAgent: navigator.userAgent }
      });

      if (response.success) {
        setSessionId(newSessionId);
        setSessionStartTime(startTime);
        setIsActive(true);
        
        // Save to localStorage
        localStorage.setItem('remoteAttendanceSession', JSON.stringify({
          sessionId: newSessionId,
          startTime: startTime.toISOString(),
          date: startTime.toISOString().slice(0, 10),
          isActive: true
        }));

        setMessage({ type: 'success', text: 'Remote work session started successfully.' });
      }
    } catch (error) {
      console.error('Error starting session:', error);
      setMessage({ type: 'error', text: 'Error starting session: ' + (error.message || 'Unknown error') });
    }
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  useEffect(() => {
    if (!message.text) return undefined;
    const timer = setTimeout(() => setMessage({ type: '', text: '' }), 2500);
    return () => clearTimeout(timer);
  }, [message]);

  return (
    <div className="remote-attendance-page">
      <div className="page-header">
        <h1><Monitor size={24} /> Remote Work</h1>
      </div>

      {message.text && (
        <div className={`remote-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="remote-work-card">
        <div className="session-status">
          <div className={`status-indicator ${isActive ? 'active' : 'inactive'}`}>
            <Activity size={16} />
            <span>{isActive ? 'Working' : 'Not Started'}</span>
          </div>
          
          <div className="duration-display">
            <Clock size={20} />
            <span className="duration">{duration}</span>
          </div>
        </div>

        <div className="session-controls">
          <button
            className={`control-btn start ${isActive ? 'disabled' : ''}`}
            onClick={startSession}
            disabled={isActive}
          >
            <Play size={18} />
            Start Session
          </button>
          
          <button
            className={`control-btn stop ${!isActive ? 'disabled' : ''}`}
            onClick={() => setShowStopConfirm(true)}
            disabled={!isActive || stopping}
          >
            <Square size={18} />
            Stop Session
          </button>
        </div>

        {isActive && (
          <div className="session-info">
            <div className="info-row">
              <span className="label">Session ID:</span>
              <span className="value session-id">{sessionId}</span>
            </div>
            <div className="info-row">
              <span className="label">Started:</span>
              <span className="value">{sessionStartTime?.toLocaleTimeString()}</span>
            </div>
            <div className="info-row">
              <span className="label">Device:</span>
              <span className="value">{deviceInfo.platform}</span>
            </div>
            <p className="session-note">
              The session keeps running until you click "Stop Session", even if you close this page or work in other windows.
            </p>
          </div>
        )}

        {!isActive && (
          <div className="remote-instructions">
            <h3><Globe size={16} /> Remote Work Instructions</h3>
            <ul>
              <li>Click "Start Session" to begin remote work</li>
              <li>Your session tracks your work duration</li>
              <li>It does not stop by itself: you can close this page or work in other windows</li>
              <li>Click "Stop Session" when you have finished: the session is then completed and saved</li>
            </ul>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showStopConfirm}
        title="Stop remote work"
        message={`Stop and complete this session? Time worked: ${duration}.`}
        onConfirm={endSession}
        onCancel={() => setShowStopConfirm(false)}
        confirmText="Stop and complete"
        cancelText="Keep working"
      />
    </div>
  );
};

export default RemoteAttendance;
